/**
 * Library of miscellaneous functions used for a11y
 */
const A11yModal = (function () {
    let focusableElements, firstFocusableElement, lastFocusableElement;
    let _LOG_COLOR = 'maroon';

    return {
        addA11yStyle: _addA11yStyle,
        closeModal: _closeModal,
        initA11yModal: _initA11yModal,
        recalculateTabStops: _recalculateTabStops,
        trapFocusInModal: _trapFocusInModal
    };

    function _addA11yStyle(modalId, options) {
        let modalElem = $('#' + modalId);

        $(modalElem).addClass('ah-modal');
        $(modalElem).attr('aria-labelledby', modalId + '_h1');

        if (options) {
            // set standard width (at some point we may to default to small, medium or large)
            if (options.modalWidth) {
                $(modalElem).css('width', '');
                $(modalElem).addClass(options.modalWidth);
            }
            $(modalElem).addClass(options.modalWidth ? options.modalWidth : '');

            let windowWidth = $(window).width();
            let modalWidth = $(modalElem).outerWidth();
            let leftPx = (windowWidth - modalWidth) / 2;
            $(modalElem).css('left', leftPx + 'px');    // center modal horizontally

            // add subtitle if present
            if (options.headerSubtitle) {
                $(modalElem).find('.portletHead .ah-modal-title').after('<span class="ah-modal-subtitle"><h2 id="' + modalId + '_h2">' + options.headerSubtitle + '</h2></span>');
            }

            // add header icon if present
            if (options.icon) {
                $(modalElem).find('.portletHead .ah-modal-title h1').before(options.icon);
            }
        }
    }

    /**
     * Clean-up DOM when closing a modal
     */
    function _closeModal() {
        lastFocusableElement = null;
        firstFocusableElement = null;
        focusableElements = null;

        // if there are additional modals open after closing this one, run the trapFocusInModal routine again
        let visibleModals = $('.ah-modal').filter(':visible');
        if (visibleModals.length > 0) {
            _trapFocusInModal(visibleModals[0]);
        }
    }

    /**
     * Function to dynamically give a11y support to a modal dialog. Will automatically set focus to the modal dialog container itself,
     * unless an initialFocusedElemId is passed in. If you call this function, you do NOT also need to call trapFocusInModal()
     * @param {string} modalId - the element id of the modal dialog
     * @param {string} [strFocusableElements] - (OPTIONAL) comma-separated list of CSS selectors for each element that can receive focus.
     *                                          If not passed in, we will attempt to find all focusable content programmatically
     * @param {string} [initialFocusedElemId] - (OPTIONAL) css selector of element you want to give initial focus to (default: modal container)
     */
    function _initA11yModal(modalId, strFocusableElements, initialFocusedElemId) {
        let modal = $('#' + modalId);
        modal.attr({ tabindex: '-1', role: 'dialog', 'aria-modal': 'true' });

        A11yUtil.setFocus(initialFocusedElemId ? initialFocusedElemId : modalId, 100);
        _trapFocusInModal(modalId, strFocusableElements);

        A11yUtil.fixA11yViolationsOnScreenLoad('#' + modalId);
    }

    /**
     * Function to recalculate the tab stops (mainly the last stop) on a modal dialog when something happens that causes a new tab stop to
     * appear after the previously calculated last tab stop. E.g. maybe the user expanded an accordion that added additional tab stops to
     * the end of the loop. This is really just a convenience function instead of calling trapFocusInModal() directly. It might be a little
     * less confusing and more intuitive to call this function.
     * @param {any} modalId
     */
    function _recalculateTabStops(modalId) {
        _logDebug('recalculateTabStops(' + modalId + ') BEFORE:', { firstFocusableElement: firstFocusableElement, lastFocusableElement: lastFocusableElement });
        _trapFocusInModal(modalId);
        _logDebug('recalculateTabStops(' + modalId + ') AFTER:', { firstFocusableElement: firstFocusableElement, lastFocusableElement: lastFocusableElement });
    }

    /**
     * This function will allow us to trap focus in a modal dialog (or some other container), such
     * that tabbing will not leave the modal, rather it will keep looping around the dialog. If you do not pass in strFocusableElements,
     * this function will attempt to find all focusable elements for you.
     *
     * NOTE: both the first and last focusable element must be displayed in the DOM (i.e. cannot be hidden), or else the looping will
     *       not work correctly. This is a problem for some dialogs I have found already.... seems GC makes heavy use of hidden and/or
     *       display=none in their dialogs.
     *
     * @param {string|object} modalElemOrId - the element id (or the element itself) of the modal (or container in which to trap focus)
     * @param {string} strFocusableElements - comma-separated list of CSS selectors for each element that can receive focus
     *                  in your modal (in logical tabbing order)
     */
    function _trapFocusInModal(modalElemOrId, strFocusableElements) {
        let modalElem = typeof modalElemOrId === 'string' ? document.querySelector('#' + modalElemOrId) : modalElemOrId;

        if (!strFocusableElements) {
            // if no focusable element selectors passed in, attempt to find our own
            _logDebug('trapFocusInModal(' + modalElemOrId + ') No focusable content selectors passed in, attempting to find automatically');
            focusableElements = $(modalElem).find('button:visible, input[type="button"]:visible, input[type="text"]:visible, input[type="radio"]:visible, input[type="checkbox"]:visible, textarea:visible, select:visible, a:visible, div[tabindex="0"]:visible, span[tabindex="0"]:visible, tr[role="button"]:visible');
        } else {
            _logDebug('trapFocusInModal(' + modalElemOrId + ') strFocusableElements:', strFocusableElements);
            focusableElements = modalElem.querySelectorAll(strFocusableElements);
        }

        firstFocusableElement = focusableElements[0]; // get first element to be focused inside container
        lastFocusableElement = focusableElements[focusableElements.length - 1]; // get last element to be focused inside container

        _logDebug('trapFocusInModal(' + modalElemOrId + ')', { firstFocusableElement: firstFocusableElement, lastFocusableElement: lastFocusableElement, focusableElements: focusableElements });

        modalElem.removeEventListener('keydown', modalTabEventHandler);
        modalElem.addEventListener('keydown', modalTabEventHandler);
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    function _logDebug(message, object) {
        DevUtils.logDebug(message, 'A11yModal', object, _LOG_COLOR);
    }

    function modalTabEventHandler(e) {
        let isTabPressed = e.key === 'Tab' || e.keyCode === A11yUtil.KEYCODES.tab;

        if (isTabPressed) {
            if (e.shiftKey) {
                // shift key pressed for shift + tab combination (i.e. tab backward)
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus(); // add focus for the last focusable element
                    e.preventDefault();
                }
            } else {
                // tab key was pressed (i.e. tab forward)
                if (document.activeElement === lastFocusableElement) {
                    // if focused has reached to last focusable element then focus first focusable element after pressing tab
                    firstFocusableElement.focus(); // add focus for the first focusable element
                    e.preventDefault();
                }
            }
        }
    }

})();

